import { useEffect, useState, useCallback } from "react";
import { useWidgetStore } from "@/stores/widget-store";
import { updateBanner } from "@/services/widget";

const REVIEW_BANNER_ID = "review-banner";
const REVIEW_DELAY_DAYS = 7;

function hasReachedReviewDelay(installDate: number): boolean {
  if (!installDate) {
    return false;
  }

  const elapsedMs = Date.now() - installDate * 1000;
  return elapsedMs >= REVIEW_DELAY_DAYS * 24 * 60 * 60 * 1000;
}

export const useReviewBannerVisibility = () => {
  const banners = useWidgetStore((state) => state.config.banners || {});
  const fetchConfig = useWidgetStore((state) => state.fetchConfig);
  const installDate = window.cyA11yGlobals.reviewBanner?.installDate ?? 0;

  const checkVisibility = useCallback(() => {
    if (!hasReachedReviewDelay(installDate)) {
      return false;
    }

    const bannerData = banners[REVIEW_BANNER_ID];

    if (!bannerData) {
      return true;
    }

    if (bannerData.status !== false) {
      return true;
    }

    if (typeof bannerData.until === "number") {
      return bannerData.until <= Math.floor(Date.now() / 1000);
    }

    return false;
  }, [banners, installDate]);

  const [isVisible, setIsVisible] = useState(checkVisibility());

  useEffect(() => {
    setIsVisible(checkVisibility());
  }, [checkVisibility]);

  const hideTemporarily = async () => {
    await updateBanner(REVIEW_BANNER_ID, {
      status: false,
      until: Math.floor(Date.now() / 1000) + 60 * 24 * 60 * 60,
    });
    setIsVisible(false);
    await fetchConfig();
  };

  const hidePermanently = async () => {
    await updateBanner(REVIEW_BANNER_ID, {
      status: false,
    });
    setIsVisible(false);
    await fetchConfig();
  };

  return {
    isVisible,
    hidePermanently,
    hideTemporarily,
  };
};
